<script setup lang="ts">
import { ref, watch, nextTick } from 'vue'
import { useCitations } from '@/composables/useCitations'
import { getRiskColor, getRiskTextColor, getRiskLabel } from '@/lib/riskTier'

const { activeCitations, focusRequest, closePanel } = useCitations()

// MCP probe references (mcp://) have no real URL; MCP tool results such as
// travel advisories can. Only http(s) sources render as links.
function isLinkable(url?: string | null): boolean {
  return !!url && /^https?:\/\//i.test(url.trim())
}

const listEl = ref<HTMLElement | null>(null)
const highlightedId = ref<number | null>(null)
let highlightTimer: ReturnType<typeof setTimeout> | null = null

watch(
  () => focusRequest.value,
  async (req) => {
    if (!req) return
    await nextTick()
    const el = listEl.value?.querySelector<HTMLElement>(`[data-ref-id="${req.id}"]`)
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' })
      highlightedId.value = req.id
      if (highlightTimer) clearTimeout(highlightTimer)
      highlightTimer = setTimeout(() => { highlightedId.value = null }, 2000)
    }
  },
)
</script>

<template>
  <aside class="flex flex-col bg-white border-l border-neutral-200 overflow-hidden">
    <div class="flex items-center justify-between gap-2 px-3 py-2.5 min-h-[48px] border-b border-neutral-200 shrink-0">
      <h2 class="text-[11px] uppercase tracking-wider font-semibold text-neutral-500">
        References ({{ activeCitations.length }})
      </h2>
      <button
        type="button"
        class="rounded p-0.5 text-neutral-400 transition-colors hover:bg-neutral-100 hover:text-neutral-700"
        title="Close references"
        aria-label="Close references"
        @click="closePanel"
      >
        <svg class="h-3.5 w-3.5" viewBox="0 0 20 20" fill="none" stroke="currentColor" stroke-width="1.8" aria-hidden="true">
          <path d="M5 5l10 10M15 5L5 15" stroke-linecap="round" />
        </svg>
      </button>
    </div>

    <ol ref="listEl" class="flex-1 overflow-y-auto p-3 space-y-2">
      <li
        v-if="activeCitations.length === 0"
        class="text-center text-xs text-neutral-400 py-6 list-none"
      >
        No references for this answer.
      </li>
      <li
        v-for="c in activeCitations"
        :key="c.id"
        :data-ref-id="c.id"
        class="flex gap-2 rounded-md border p-2.5 transition-colors list-none"
        :class="highlightedId === c.id ? 'border-blue-400 bg-blue-50' : 'border-neutral-200'"
      >
        <span class="flex items-center gap-1.5 shrink-0 pt-0.5">
          <!-- Always reserve the tier dot (N/A grey when unset) so MCP / no-tier
               refs stay column-aligned with risk-tiered portal sources. -->
          <span
            class="inline-block h-2 w-2 rounded-full"
            :style="{ backgroundColor: getRiskColor(c.riskTier) }"
            aria-hidden="true"
          />
          <span class="text-[12px] font-semibold tabular-nums text-neutral-500">{{ c.id }}</span>
        </span>
        <div class="min-w-0 flex-1">
          <div
            v-if="c.riskTier != null"
            class="text-[11px] font-medium"
            :style="{ color: getRiskTextColor(c.riskTier) }"
          >
            {{ getRiskLabel(c.riskTier) }}
          </div>
          <div class="text-[13px] font-medium text-neutral-800 leading-tight">
            <a
              v-if="isLinkable(c.url)"
              :href="c.url"
              target="_blank"
              rel="noopener noreferrer"
              class="hover:text-blue-600 hover:underline"
            >
              {{ c.title }}
            </a>
            <span v-else>{{ c.title }}</span>
          </div>
          <div
            v-if="!isLinkable(c.url) && (c.sourceType === 'mcp' || (c.url && c.url.startsWith('mcp://')))"
            class="mt-0.5 text-[11px] text-neutral-500"
          >
            MCP call · no link
          </div>
        </div>
      </li>
    </ol>
  </aside>
</template>
